import React, { useState } from 'react';

import { Card, Collapse } from 'antd';
import { useTranslation } from 'react-i18next';

import RecordingDiagnostics from './RecordingDiagnostics';
import VideoPreview from './VideoPreview';

// Shape of the service layer this component depends on. Both
// services/{admin,operator}/orderService and services/operator/fulfillmentService
// satisfy this — keeps the component re-usable across Order- and
// Fulfillment-scoped pages without duck-typing inside the component.
interface VideoSectionService {
  getVideos: (id: string) => Promise<any>;
  getRecordingSessions: (id: string) => Promise<any>;
  forceFinalizeRecordingSession: (sessionId: string) => Promise<any>;
}

interface Props {
  // Order ID or Fulfillment ID — whichever the supplied service expects.
  resourceId: string;
  service: VideoSectionService;
  // Project-wide background-job notification helper. When present, force-merge
  // jobs are registered so completion / failure surface in the right-corner
  // panel; when absent (e.g. caller hasn't wired it), the rescue button still
  // works but won't show up in the job tray.
  addJob?: (jobId: string, jobName: string) => void;
  // Card title override — defaults to t('shared.video_preview.title').
  title?: string;
}

// VideoSection — shared Card composing:
//   1. <VideoPreview/>     finalized FulfillmentVideos
//   2. Collapse → <RecordingDiagnostics/>  diagnostics + force-merge rescue
//
// Used by:
//   - shared/orders/OrderShow.tsx        (scope = order)
//   - operator/Fulfillments/Show.tsx     (scope = fulfillment)
//
// The diagnostics panel mounts lazily on first expand so the GCS list-objects
// call (one per session) doesn't fire on every page load.
const VideoPreviewAny = VideoPreview as any;

export default function VideoSection({ resourceId, service, addJob, title }: Props) {
  const { t } = useTranslation();
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);

  return (
    <Card
      title={title ?? t('shared.video_preview.title', { defaultValue: '影片' })}
      style={{ marginBottom: '16px' }}
      size="small"
    >
      <VideoPreviewAny orderId={resourceId} orderService={service} />
      <Collapse
        ghost
        size="small"
        style={{ marginTop: 8 }}
        activeKey={diagnosticsOpen ? ['recording-diagnostics'] : []}
        onChange={(keys) => {
          const arr = Array.isArray(keys) ? keys : [keys];
          setDiagnosticsOpen(arr.includes('recording-diagnostics'));
        }}
        items={[
          {
            key: 'recording-diagnostics',
            label: t('shared.recording_diagnostics.title', { defaultValue: '錄影 Session 診斷與救援' }),
            children: diagnosticsOpen ? (
              <RecordingDiagnostics orderId={resourceId} orderService={service} addJob={addJob} />
            ) : null,
          },
        ]}
      />
    </Card>
  );
}
